import type { CelebrationFeatureProfile, CelebrantEntry } from '@om/shared-types';
import * as celebrantConfig from '@om/shared-types/celebrant-config';
const { toCelebrantArrays } = celebrantConfig;
import type { PublicQuickCreateCelebrationDto } from '@/lib/api/celebration.service';

export interface QuickCreateFormValues {
  title: string;
  celebrationType: string;
  celebrationSubtype?: string | null;
  isMultiEvent: boolean;
  eventDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  locationName: string;
  hostName: string;
  hostEmail: string;
  celebrantName: string;
  celebrantRole?: string;
  celebrantEmail: string;
  additionalCelebrants?: CelebrantEntry[];
  isSurprise?: boolean;
  featureProfile: CelebrationFeatureProfile;
  description?: string;
  rsvpDeadline?: string;
  maxGuests?: number;
  plusOnesAllowed?: boolean;
  plusOnesLimit?: number;
  accessType?: 'open' | 'invite_only' | 'registered';
  guestMode?: 'link' | 'email' | 'detailed';
  guestEmails?: string[];
  emailDispatchMode?: 'immediate' | 'draft';
  guestCountEstimate?: number;
}

export type QuickCreatePayloadResult =
  | { ok: true; payload: PublicQuickCreateCelebrationDto }
  | { ok: false; error: string };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const getEffectiveCelebrantEmail = ({
  hostEmail,
  celebrantEmail,
  isSurprise,
}: {
  hostEmail: string;
  celebrantEmail: string;
  isSurprise?: boolean;
}): string => {
  const host = hostEmail.trim();
  const celebrant = celebrantEmail.trim();

  if (isSurprise) {
    return host || celebrant;
  }

  return celebrant || host;
};

export const buildQuickCreatePayload = (values: QuickCreateFormValues): QuickCreatePayloadResult => {
  const title = values.title.trim();
  if (!title) {
    return { ok: false, error: 'Please enter an event title.' };
  }

  const hostName = values.hostName.trim();
  const hostEmail = values.hostEmail.trim();

  // The celebrant defaults to the host, but is never synthesized as a placeholder.
  const celebrantName = values.celebrantName.trim() || hostName;
  const celebrantEmail = getEffectiveCelebrantEmail({
    hostEmail,
    celebrantEmail: values.celebrantEmail,
    isSurprise: values.isSurprise,
  });

  if (!celebrantName) {
    return { ok: false, error: 'Please enter who this celebration is for.' };
  }

  if (celebrantEmail && !EMAIL_PATTERN.test(celebrantEmail)) {
    return { ok: false, error: `Please enter a valid email address for "${celebrantName}".` };
  }

  if (hostEmail && !EMAIL_PATTERN.test(hostEmail)) {
    return { ok: false, error: 'Please enter a valid host email address.' };
  }

  const endDate = values.isMultiEvent ? values.endDate : values.eventDate;

  const combinedCelebrants = toCelebrantArrays([
    { name: celebrantName, role: values.celebrantRole ?? '', email: celebrantEmail },
    ...(values.additionalCelebrants ?? []),
  ]);

  return {
    ok: true,
    payload: {
      title,
      celebration_type: values.celebrationType,
      celebration_subtype: values.celebrationSubtype || undefined,
      description: values.description?.trim() || undefined,
      event_title: title,
      event_type: values.isMultiEvent ? 'multi_event' : 'other',
      event_date: values.eventDate,
      start_time: `${values.eventDate}T${values.startTime}:00.000Z`,
      end_time: `${endDate}T${values.endTime}:00.000Z`,
      location_name: values.locationName.trim() || undefined,
      host_name: hostName || undefined,
      host_email: hostEmail || undefined,
      celebrant_names: combinedCelebrants.names,
      celebrant_roles: combinedCelebrants.roles,
      celebrant_emails: combinedCelebrants.emails.some(Boolean) ? combinedCelebrants.emails : undefined,
      is_surprise: values.isSurprise ?? false,
      feature_profile: values.featureProfile,
      rsvp_deadline: values.rsvpDeadline?.trim() || undefined,
      max_guests: values.maxGuests,
      plus_ones_allowed: values.plusOnesAllowed,
      plus_ones_limit: values.plusOnesAllowed ? (values.plusOnesLimit || 1) : undefined,
      access_type: values.accessType,
      guest_mode: values.guestMode,
      guest_emails: values.guestEmails,
      email_dispatch_mode: values.emailDispatchMode,
      guest_count_estimate: values.guestCountEstimate,
    },
  };
};
